// --- React
import React from "react";
// --- UI Components
import { MultiSelect } from "@dtci/spellbook/MultiSelect";

/**
 * EditableMultiSelectRenderer Component
 *
 * Cell renderer for AG Grid that shows a multi-select dropdown when in edit mode,
 * or pipe-separated text when not editing.
 *
 * @param {Array} options - Dropdown options [{label, value}]
 * @param {Function} getEditingRows - Function that returns current editing rows state
 * @param {Function} onValueChange - Callback when value changes (data, fieldName, newValue)
 * @param {string} fieldName - The field name being edited
 * @param {Function} onLanguageSelect - Optional callback to track last selected language
 * @param {Function} getLastSelectedLanguages - Optional getter for last selected languages per row
 * @returns {Function} The cell renderer component
 */
export const EditableMultiSelectRenderer = (
  options,
  getEditingRows,
  onValueChange,
  fieldName,
  onLanguageSelect,
  getLastSelectedLanguages,
) => {
  const Renderer = ({ value, data }) => {
    const editingRows = getEditingRows ? getEditingRows() : {};
    const isEditing = editingRows[data?.id];

    // Get last selected value for this row/field (for highlighting)
    const lastSelectedLanguages = getLastSelectedLanguages
      ? getLastSelectedLanguages()
      : {};
    const lastSelectedValue =
      lastSelectedLanguages[data?.id]?.[fieldName] || null;

    // Parse pipe-separated string into array of selected options
    const parseValue = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;

      const values = String(val)
        .split("|")
        .map((v) => v.trim())
        .filter(Boolean);
      return values.map((v) => {
        const found = options?.find(
          (opt) => opt.label === v || opt.value === v,
        );
        return found || { label: v, value: v };
      });
    };

    // Format display for non-edit mode
    const formatDisplayValue = (val) => {
      if (!val) return "";
      if (Array.isArray(val)) {
        return val.map((v) => v.label || v).join(" | ");
      }
      return String(val).replaceAll("|", " | ");
    };

    if (!isEditing) {
      return <span>{formatDisplayValue(value)}</span>;
    }

    const selectedOptions = parseValue(value);

    const handleChange = (selected, actionMeta) => {
      // Convert back to pipe-separated string for storage
      const newValue =
        selected?.map((opt) => opt.label || opt.value).join("|") || "";
      if (onValueChange) {
        onValueChange(data, fieldName, newValue);
      }

      // Track last selected language for highlighting (only on select action)
      if (
        onLanguageSelect &&
        actionMeta?.action === "select-option" &&
        actionMeta?.option
      ) {
        const selectedLabel =
          actionMeta.option.label || actionMeta.option.value;
        onLanguageSelect(data?.id, fieldName, selectedLabel);
      }
    };

    return (
      <div style={{ minWidth: 150, width: "auto", display: "inline-block" }}>
        <MultiSelect
          options={options || []}
          value={selectedOptions}
          onChange={handleChange}
          isSearchable
          isClearable
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            container: (base) => ({ ...base, minWidth: 150, width: "auto" }),
            control: (base) => ({
              ...base,
              minHeight: 32,
              minWidth: 150,
              width: "auto",
              height: "auto",
              alignItems: "flex-start",
            }),
            menu: (base) => ({ ...base, minWidth: 200, width: "max-content" }),
            menuList: (base) => ({ ...base, maxHeight: 300 }),
            multiValue: (base, state) => ({
              ...base,
              margin: 2,
              // Highlight last selected chip with subtle amber background
              ...(lastSelectedValue && state.data?.label === lastSelectedValue
                ? { backgroundColor: "#FEF3C7" }
                : {}),
            }),
            valueContainer: (base) => ({
              ...base,
              flexWrap: "wrap",
              padding: 4,
            }),
          }}
        />
      </div>
    );
  };

  return Renderer;
};
